import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Play,
  Image as ImageIcon,
  Lock,
  Eye,
  Download,
  Share2,
  Heart,
  Filter,
  Grid3X3,
  List,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
  User,
} from 'lucide-react'

interface GalleryItem {
  id: string
  type: 'photo' | 'video'
  title: string
  category: string
  thumbnail: string
  fullSize: string
  description: string
  date: string
  location: string
  client: string
  isProtected: boolean
  password?: string
  likes: number
  views: number
}

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null)
  const [passwordInput, setPasswordInput] = useState('')
  const [unlockedItems, setUnlockedItems] = useState<Set<string>>(new Set())
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [pendingItem, setPendingItem] = useState<GalleryItem | null>(null)

  const categories = [
    { id: 'all', name: 'All', count: 48 },
    { id: 'weddings', name: 'Weddings', count: 18 },
    { id: 'corporate', name: 'Corporate', count: 12 },
    { id: 'events', name: 'Events', count: 10 },
    { id: 'portraits', name: 'Portraits', count: 8 },
  ]

  // Sample gallery data
  const galleryItems: GalleryItem[] = [
    {
      id: '1',
      type: 'photo',
      title: 'Elegant Wedding Ceremony',
      category: 'weddings',
      thumbnail: '/gallery/wedding-1-thumb.jpg',
      fullSize: '/gallery/wedding-1-full.jpg',
      description: 'Beautiful outdoor wedding ceremony with stunning floral arrangements',
      date: '2024-01-15',
      location: 'Garden Valley Resort',
      client: 'Sarah & Michael',
      isProtected: true,
      password: 'sarah2024',
      likes: 45,
      views: 234,
    },
    {
      id: '2',
      type: 'video',
      title: 'Corporate Product Launch',
      category: 'corporate',
      thumbnail: '/gallery/corporate-1-thumb.jpg',
      fullSize: '/gallery/corporate-1-video.mp4',
      description: 'Dynamic product launch event with live demonstrations',
      date: '2024-01-20',
      location: 'Tech Convention Center',
      client: 'TechCorp Industries',
      isProtected: false,
      likes: 32,
      views: 156,
    },
    {
      id: '3',
      type: 'photo',
      title: 'Birthday Celebration',
      category: 'events',
      thumbnail: '/gallery/birthday-1-thumb.jpg',
      fullSize: '/gallery/birthday-1-full.jpg',
      description: 'Joyful 50th birthday celebration with family and friends',
      date: '2024-01-25',
      location: 'Private Residence',
      client: 'Johnson Family',
      isProtected: true,
      password: 'birthday50',
      likes: 28,
      views: 89,
    },
    {
      id: '4',
      type: 'video',
      title: 'Professional Headshots',
      category: 'portraits',
      thumbnail: '/gallery/portrait-1-thumb.jpg',
      fullSize: '/gallery/portrait-1-video.mp4',
      description: 'Executive headshot session for corporate branding',
      date: '2024-02-01',
      location: 'Studio Downtown',
      client: 'Executive Team',
      isProtected: false,
      likes: 19,
      views: 67,
    },
    // Add more sample items...
  ]

  const filteredItems = galleryItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.client.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const handleItemClick = (item: GalleryItem) => {
    if (item.isProtected && !unlockedItems.has(item.id)) {
      setPendingItem(item)
      setShowPasswordModal(true)
    } else {
      setSelectedItem(item)
    }
  }

  const handlePasswordSubmit = () => {
    if (pendingItem && passwordInput === pendingItem.password) {
      setUnlockedItems(prev => new Set([...prev, pendingItem.id]))
      setSelectedItem(pendingItem)
      setShowPasswordModal(false)
      setPasswordInput('')
      setPendingItem(null)
    } else {
      // Show error - in a real app, you'd show a proper error message
      alert('Incorrect password')
    }
  }

  const closeModal = () => {
    setShowPasswordModal(false)
    setPasswordInput('')
    setPendingItem(null)
  }

  const closeViewer = () => {
    setSelectedItem(null)
  }

  const navigateItem = (direction: 'prev' | 'next') => {
    if (!selectedItem) return
    
    const currentIndex = filteredItems.findIndex(item => item.id === selectedItem.id)
    let newIndex
    
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredItems.length - 1
    } else {
      newIndex = currentIndex < filteredItems.length - 1 ? currentIndex + 1 : 0
    }
    
    const newItem = filteredItems[newIndex]
    if (newItem.isProtected && !unlockedItems.has(newItem.id)) {
      setPendingItem(newItem)
      setShowPasswordModal(true)
      setSelectedItem(null)
    } else {
      setSelectedItem(newItem)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-24"
    >
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Our <span className="gradient-text">Gallery</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 max-w-4xl mx-auto leading-relaxed">
              Explore our portfolio of stunning photography and videography work. Some galleries are password-protected for client privacy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters and Controls */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <div className="glass-card mb-8">
            <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/60" />
                <input
                  type="text"
                  placeholder="Search gallery..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="glass-input pl-10 w-full"
                />
              </div>

              {/* Category Filter */}
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-white/60" />
                <div className="flex space-x-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                          : 'glass hover:bg-white/20 text-white/80'
                      }`}
                    >
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>

              {/* View Mode */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    viewMode === 'grid'
                      ? 'bg-primary-500 text-white'
                      : 'glass hover:bg-white/20 text-white/80'
                  }`}
                >
                  <Grid3X3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    viewMode === 'list'
                      ? 'bg-primary-500 text-white'
                      : 'glass hover:bg-white/20 text-white/80'
                  }`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Gallery Grid/List */}
          <motion.div
            layout
            className={`${
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                : 'space-y-4'
            }`}
          >
            <AnimatePresence>
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className={`glass-card hover-lift hover-glow group cursor-pointer ${
                    viewMode === 'list' ? 'flex items-center space-x-6' : ''
                  }`}
                  onClick={() => handleItemClick(item)}
                >
                  {/* Thumbnail */}
                  <div className={`relative overflow-hidden rounded-xl ${
                    viewMode === 'list' ? 'w-32 h-20 flex-shrink-0' : 'aspect-square mb-4'
                  }`}>
                    <div className="w-full h-full bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center">
                      {item.type === 'video' ? (
                        <Play className="w-8 h-8 text-white/60" />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-white/60" />
                      )}
                    </div>
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      {item.isProtected && !unlockedItems.has(item.id) ? (
                        <Lock className="w-6 h-6 text-white" />
                      ) : (
                        <Eye className="w-6 h-6 text-white" />
                      )}
                    </div>
                    
                    {/* Type indicator */}
                    <div className="absolute top-2 right-2">
                      {item.type === 'video' && (
                        <div className="bg-black/60 rounded-full p-1">
                          <Play className="w-4 h-4 text-white" />
                        </div>
                      )}
                      {item.isProtected && (
                        <div className="bg-accent-500 rounded-full p-1 ml-1">
                          <Lock className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className={viewMode === 'list' ? 'flex-1' : ''}>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-300 transition-colors duration-300">
                      {item.title}
                    </h3>
                    
                    {viewMode === 'list' && (
                      <p className="text-white/70 text-sm mb-2">
                        {item.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-sm text-white/60">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(item.date).toLocaleDateString()}</span>
                        </div>
                        {viewMode === 'list' && (
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-4 h-4" />
                            <span>{item.location}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          <Heart className="w-4 h-4" />
                          <span>{item.likes}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Eye className="w-4 h-4" />
                          <span>{item.views}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filteredItems.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <ImageIcon className="w-16 h-16 text-white/40 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white/60 mb-2">
                No items found
              </h3>
              <p className="text-white/40">
                Try adjusting your search or filter criteria.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card max-w-md w-full space-y-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-3 rounded-full bg-gradient-to-r from-accent-500 to-secondary-500">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Protected Gallery</h3>
                    <p className="text-white/60 text-sm">Enter password to view</p>
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors duration-300"
                >
                  <X className="w-5 h-5 text-white/70" />
                </button>
              </div>
              
              {pendingItem && (
                <div className="space-y-2">
                  <h4 className="font-medium text-white">{pendingItem.title}</h4>
                  <p className="text-white/70 text-sm">{pendingItem.description}</p>
                </div>
              )}
              
              <div className="space-y-4">
                <input
                  type="password"
                  placeholder="Enter gallery password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  className="glass-input w-full"
                  autoFocus
                />
                
                <div className="flex space-x-3">
                  <button
                    onClick={handlePasswordSubmit}
                    className="flex-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full py-3 font-medium transition-all duration-300 hover:scale-105 active:scale-95"
                  >
                    Unlock Gallery
                  </button>
                  <button
                    onClick={closeModal}
                    className="px-6 glass-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image/Video Viewer */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm"
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 glass">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <User className="w-5 h-5 text-white/60" />
                    <span className="text-white font-medium">{selectedItem.client}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-5 h-5 text-white/60" />
                    <span className="text-white/80">{selectedItem.location}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-white/60" />
                    <span className="text-white/80">{new Date(selectedItem.date).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <button className="p-2 rounded-full glass hover:bg-white/20 transition-colors duration-300">
                    <Download className="w-5 h-5 text-white/70" />
                  </button>
                  <button className="p-2 rounded-full glass hover:bg-white/20 transition-colors duration-300">
                    <Share2 className="w-5 h-5 text-white/70" />
                  </button>
                  <button
                    onClick={closeViewer}
                    className="p-2 rounded-full glass hover:bg-white/20 transition-colors duration-300"
                  >
                    <X className="w-5 h-5 text-white/70" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="relative max-w-6xl max-h-full">
                  {selectedItem.type === 'video' ? (
                    <div className="aspect-video bg-black rounded-lg flex items-center justify-center">
                      <Play className="w-16 h-16 text-white/60" />
                      <p className="absolute bottom-4 left-4 text-white/80">Video Player Placeholder</p>
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-lg flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-white/60" />
                      <p className="absolute bottom-4 left-4 text-white/80">Image Placeholder</p>
                    </div>
                  )}
                  
                  {/* Navigation */}
                  <button
                    onClick={() => navigateItem('prev')}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full glass hover:bg-white/20 transition-all duration-300"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                  <button
                    onClick={() => navigateItem('next')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full glass hover:bg-white/20 transition-all duration-300"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 glass">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold text-white mb-2">{selectedItem.title}</h2>
                  <p className="text-white/80 mb-4">{selectedItem.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center space-x-2">
                        <Heart className="w-5 h-5 text-red-400" />
                        <span className="text-white">{selectedItem.likes} likes</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Eye className="w-5 h-5 text-blue-400" />
                        <span className="text-white">{selectedItem.views} views</span>
                      </div>
                    </div>
                    
                    <div className="text-white/60">
                      {filteredItems.findIndex(item => item.id === selectedItem.id) + 1} of {filteredItems.length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default Gallery